import { SignInIdentifier } from '@logto/schemas';
import { useState, useCallback, useMemo } from 'react';
import type { ChangeEventHandler } from 'react';

import { parseIdentifierValue } from '@/utils/form';

import { detectIdentifierType } from './utils';

export type IdentifierInputType =
  | SignInIdentifier.Email
  | SignInIdentifier.Phone
  | SignInIdentifier.Username;

export type IdentifierInputValue = {
  /**
   * The type of the identifier input.
   * `undefined` value is for the case when the user has inputted an identifier but the type is not yet determined in the `SmartInputField`.
   */
  type?: IdentifierInputType;
  /**
   * The value of the identifier input.
   */
  value: string;
};

type Props = {
  defaultValue?: string;
  enabledTypes: IdentifierInputType[];
};

const useSmartInputField = ({ defaultValue, enabledTypes }: Props) => {
  const enabledTypeSet = useMemo(() => new Set(enabledTypes), [enabledTypes]);

  // Parse default type from enabled types and default value
  const defaultType = useMemo(
    () => detectIdentifierType({ value: defaultValue ?? '', enabledTypeSet }),
    [defaultValue, enabledTypeSet]
  );

  // Parse default value if provided
  const { countryCode: defaultCountryCode, inputValue: defaultInputValue } = useMemo(
    () => parseIdentifierValue(defaultType, defaultValue),
    [defaultType, defaultValue]
  );

  const [currentType, setCurrentType] = useState(defaultType);

  const [inputValue, setInputValue] = useState<string>(defaultInputValue ?? '');

  const detectInputType = useCallback(
    (value: string) => detectIdentifierType({ value, enabledTypeSet, currentType }),
    [currentType, enabledTypeSet]
  );

  const onInputValueChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    ({ target: { value } }) => {
      let trimValue = value.trim();

      const type = detectInputType(trimValue);

      if (type === SignInIdentifier.Phone || (enabledTypes.length === 1 && enabledTypes[0] === SignInIdentifier.Phone)) {
        const digitsOnly = trimValue.replace(/\D/g, '');
        trimValue = digitsOnly.slice(0, 10);
      }

      setInputValue(trimValue);
      setCurrentType(type);
    },
    [detectInputType, enabledTypes]
  );

  const onInputValueClear = useCallback(() => {
    setInputValue('');
    setCurrentType(enabledTypeSet.size === 1 ? defaultType : undefined);
  }, [defaultType, enabledTypeSet.size]);

  return {
    inputValue,
    onInputValueChange,
    onInputValueClear,
    identifierType: currentType,
  };
};

export default useSmartInputField;
